import { motion } from 'framer-motion';
import { Briefcase, ArrowRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import MarketingLayout from '../components/MarketingLayout';
import Breadcrumb from '../components/Breadcrumb';
import PageHero from '../components/PageHero';
import { getPageHeroImageUrl } from '../config/cloudflareAssets';

export default function CareersPage() {
  const location = useLocation();
  const heroImageUrl = getPageHeroImageUrl(location.pathname);

  const interestAreas = [
    {
      title: 'Engineering',
      department: 'Engineering',
      description:
        'Interested in building connected product experiences with React, Node.js, and AI? Tell us about the systems you have built and the problems you enjoy solving.',
    },
    {
      title: 'Product design',
      department: 'Design',
      description:
        'Help us think about making complex planning decisions feel clear. Share work that shows how you turn a difficult workflow into an understandable experience.',
    },
    {
      title: 'Customer success and education',
      department: 'Customer Success',
      description:
        'Interested in helping people put strategic planning into practice? Tell us about your experience explaining products, supporting customers, or creating useful learning material.',
    },
    {
      title: 'Marketing and communication',
      department: 'Marketing',
      description:
        'Bring the value of connected planning to life. Share examples of clear product storytelling, thoughtful campaigns, or content that helps people make a decision.',
    },
  ];

  const introductionPrompts = [
    'The area you would like to contribute to',
    'Your relevant experience and strengths',
    'Links to work, projects, or a portfolio',
    'The contribution you have in mind',
  ];

  return (
    <MarketingLayout>
      <div className="public-breadcrumb">
        <div className="container mx-auto px-4 py-3">
          <Breadcrumb items={[{ label: 'Careers' }]} />
        </div>
      </div>

      <PageHero
        heroImageUrl={heroImageUrl}
        title="Bring your craft to clearer strategic planning."
        subtitle="Interested in helping people turn direction into practical work? Express an interest in future opportunities with StratPlan and tell us how you could contribute."
      >
        <div className="flex flex-wrap justify-center gap-4">
          <a href="#areas-of-interest" className="public-button public-button--primary">
            Explore areas of interest
          </a>
          <Link to="/contact" className="public-button public-button--secondary">
            Introduce yourself
          </Link>
        </div>
      </PageHero>

      {/* Product and Contribution Section */}
      <section className="public-section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-section-heading text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-6">Work on the connection that matters.</h2>
              <p className="text-xl text-gray-600">
                From a founder's first vision statement to a team's next project, the product helps
                people see how their work fits a bigger purpose. There is plenty of thoughtful
                product, design and engineering work in making that connection feel simple.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {[
                {
                  title: 'Ideas into practical work',
                  description:
                    'Explore the connection from a company objective to the projects and tasks that support it.',
                },
                {
                  title: 'Design for clarity',
                  description:
                    'Make guided planning, business assessments, and strategic choices easier to understand and use.',
                },
                {
                  title: 'AI with useful context',
                  description:
                    'Think about how company context can produce draft ideas that people can question, edit, and build on.',
                },
                {
                  title: 'A connected product',
                  description:
                    'Consider the whole journey, from company direction and organisation structure to delivery and reporting.',
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="public-card public-card--blue p-7"
                >
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="public-section public-section--tint">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-section-heading text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-4">Tell us what you could bring.</h2>
              <p className="text-xl text-gray-600">
                A brief introduction and a few examples of your work help us understand where your
                experience may fit.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-4">
              {introductionPrompts.map((prompt, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="public-card flex items-center gap-3 p-5"
                >
                  <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />
                  <span className="text-gray-700">{prompt}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Expressions of Interest Section */}
      <section id="areas-of-interest" className="public-section scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-section-heading text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-4">Find where you can contribute.</h2>
              <p className="text-xl text-gray-600">
                These areas give you a starting point for an introduction. This page welcomes
                expressions of interest for future opportunities.
              </p>
            </motion.div>

            <div className="space-y-6">
              {interestAreas.map((area, index) => (
                <motion.div
                  key={index}
                  initial={false}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="public-card p-7 md:p-8"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold mb-2">{area.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Briefcase className="w-4 h-4 mr-1" />
                          {area.department} · Expression of interest
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600 mb-4">{area.description}</p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Express an interest <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* No Match CTA */}
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="public-card public-card--purple mt-12 text-center p-8"
            >
              <h3 className="text-2xl font-bold mb-4">Have a different strength to bring?</h3>
              <p className="text-gray-600 mb-6">
                Introduce yourself through our contact page. Share your background, relevant work,
                and the contribution you have in mind, even if it falls outside these areas.
              </p>
              <Link to="/contact" className="public-button public-button--primary">
                Introduce yourself
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
